import React, { useEffect, useState, useMemo } from "react";

import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  message,
  Row,
  Select,
  Table,
  Typography,
} from "antd";
import fileDownload from "js-file-download";
import moment from "moment";

import useDebounce from "../../hooks/useDebounce";
import { getInvoices } from "../api";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;

const invoiceTypeOptions = [
  { label: "Order", value: "order" },
  { label: "Creator Tier", value: "creator-tier" },
  { label: "Mango Credit Transaction", value: "mango-credit-transaction" },
];

const countryOptions = [
  { label: "Indian", value: "indian" },
  { label: "International", value: "international" },
];

const Invoices = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [dateRange, setDateRange] = useState([
    moment().subtract(30, "days").startOf("day"),
    moment().endOf("day"),
  ]);
  const [invoiceType, setInvoiceType] = useState("order");
  const [country, setCountry] = useState("");
  const [tableKey, setTableKey] = useState(Date.now());

  const debouncedSearch = useDebounce({
    value: search,
    callback: () => setPage(1),
  });

  const getInvoicesData = async (csvDownload = false) => {
    setLoading(true);
    try {
      const startDate = dateRange?.[0]?.toISOString() || "";
      const endDate = dateRange?.[1]?.toISOString() || "";

      const resp = await getInvoices(
        invoiceType,
        startDate,
        endDate,
        debouncedSearch,
        csvDownload ? undefined : page,
        csvDownload ? undefined : limit,
        csvDownload,
        country
      );

      if (csvDownload) {
        fileDownload(
          resp,
          `invoices-${invoiceType}-${moment().format("YYYY-MM-DD")}.csv`
        );

        return;
      }

      if (resp?.code === 200) {
        setData(resp.result?.data || []);
        setTotal(resp.result?.total || 0);
        setTableKey(Date.now());
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getInvoicesData();
  }, [page, limit, debouncedSearch, dateRange, invoiceType, country]);

  const columns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
      },
      {
        title: "Phone",
        dataIndex: "phone",
        key: "phone",
      },
      {
        title: "Country",
        dataIndex: "country",
        key: "country",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (text) => {
          let color = "inherit";
          if (text === "completed") color = "#52c41a";
          if (text === "refunded") color = "#f5222d";

          return <span style={{ color }}>{text || "-"}</span>;
        },
      },
      ...(invoiceType === "creator-tier" ||
      invoiceType === "mango-credit-transaction"
        ? [
            {
              title: "Invoice",
              dataIndex: "invoice",
              key: "invoice",
              render: (text) =>
                text ? (
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => window.open(text, "_blank")}
                  >
                    View Invoice
                  </Button>
                ) : (
                  "-"
                ),
            },
          ]
        : []),
      ...(invoiceType === "order" || invoiceType === "mango-credit-transaction"
        ? [
            {
              title: "Convenience Fee Invoice",
              dataIndex: "convenienceFeeInvoice",
              key: "convenienceFeeInvoice",
              render: (text) =>
                text ? (
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => window.open(text, "_blank")}
                  >
                    View Invoice
                  </Button>
                ) : (
                  "-"
                ),
            },
          ]
        : []),
      ...(invoiceType === "order"
        ? [
            {
              title: "Commission Invoice",
              dataIndex: "commissionInvoice",
              key: "commissionInvoice",
              render: (text) =>
                text ? (
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => window.open(text, "_blank")}
                  >
                    View Invoice
                  </Button>
                ) : (
                  "-"
                ),
            },
          ]
        : []),
      {
        title: "Created At",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (text) => moment(text).format("DD/MM/YYYY HH:mm:ss"),
      },
    ],
    [invoiceType]
  );

  return (
    <div style={{ padding: "20px" }}>
      <Title level={2}>Invoices</Title>

      <Card style={{ marginBottom: "20px" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: "8px" }}>
              <Text strong>Date Range</Text>
            </div>
            <RangePicker
              style={{ width: "100%" }}
              value={dateRange}
              onChange={(dates) => {
                if (dates) {
                  setDateRange([
                    dates[0]?.startOf("day"),
                    dates[1]?.endOf("day"),
                  ]);
                } else {
                  setDateRange([
                    moment().subtract(30, "days").startOf("day"),
                    moment().endOf("day"),
                  ]);
                }
              }}
              allowClear={false}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: "8px" }}>
              <Text strong>Invoice Type</Text>
            </div>
            <Select
              style={{ width: "100%" }}
              value={invoiceType}
              onChange={setInvoiceType}
              options={invoiceTypeOptions}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: "8px" }}>
              <Text strong>Country</Text>
            </div>
            <Select
              style={{ width: "100%" }}
              value={country}
              onChange={setCountry}
              options={countryOptions}
              placeholder="All Countries"
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: "8px" }}>
              <Text strong>Search</Text>
            </div>
            <Search
              placeholder="Search by name, email, phone"
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: "8px" }}>
              <Text strong>Export</Text>
            </div>
            <Button
              type="primary"
              onClick={() => getInvoicesData(true)}
              loading={loading}
            >
              Download CSV
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        key={tableKey}
        columns={columns}
        dataSource={data}
        rowKey={(record) =>
          record.email || record.phone || Math.random().toString()
        }
        pagination={{
          current: page,
          pageSize: limit,
          total,
          onChange: (newPage) => setPage(newPage),
          onShowSizeChange: (_, newSize) => {
            setLimit(newSize);
            setPage(1);
          },
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (totalCount) => `Total ${totalCount} invoices`,
        }}
        loading={loading}
        scroll={{ x: 1200 }}
      />
    </div>
  );
};

export default Invoices;
